import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

const theme = extendTheme({
  config,
  fonts: {
    heading: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
    mono: `'JetBrains Mono', 'Fira Code', 'Consolas', 'Liberation Mono', 'Courier New', monospace`,
  },
  colors: {
    // Primary Brand Colors - Deep Blue for Maximum Contrast
    brand: {
      50: '#e6f3ff',
      100: '#cce7ff',
      200: '#99cfff',
      300: '#66b7ff',
      400: '#339fff',
      500: '#0066cc', // Primary brand color - high contrast
      600: '#0052a3',
      700: '#003d7a',
      800: '#002952',
      900: '#001429',
    },
    
    // Secondary Colors - Rich Purple for Accents
    secondary: {
      50: '#f3e8ff',
      100: '#e7d1ff',
      200: '#cfa3ff',
      300: '#b775ff',
      400: '#9f47ff',
      500: '#7c3aed', // Secondary color - high contrast
      600: '#6b21a8',
      700: '#5b1b8a',
      800: '#4a156c',
      900: '#390f4e',
    },
    
    // Accent Colors - Deep Teal for Success
    accent: {
      50: '#e6f7f5',
      100: '#ccefeb',
      200: '#99dfd7',
      300: '#66cfc3',
      400: '#33bfaf',
      500: '#0d9488', // Accent color - high contrast
      600: '#0a7670',
      700: '#075858',
      800: '#053a40',
      900: '#021c28',
    },
    
    // High Contrast Gray Scale - Optimized for Light Mode
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    
    // Success Colors - Deep Green
    success: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#16a34a', // Success color - high contrast
      600: '#15803d',
      700: '#166534',
      800: '#14532d',
      900: '#0f3d22',
    },
    
    // Warning Colors - Deep Amber
    warning: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#d97706', // Warning color - high contrast
      600: '#b45309',
      700: '#92400e',
      800: '#78350f',
      900: '#5c2a0c',
    },
    
    // Error Colors - Deep Red
    error: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#dc2626', // Error color - high contrast
      600: '#b91c1c',
      700: '#991b1b',
      800: '#7f1d1d',
      900: '#5f1616',
    },
    
    // Info Colors - Deep Blue
    info: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#2563eb', // Info color - high contrast
      600: '#1d4ed8',
      700: '#1e40af',
      800: '#1e3a8a',
      900: '#1e2b6b',
    },
  },
  
  // Enhanced Component Styles with Better Contrast
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'blue',
      },
      variants: {
        solid: {
          bg: 'brand.500',
          color: 'white',
          fontWeight: '600',
          _hover: {
            bg: 'brand.600',
            transform: 'translateY(-1px)',
            boxShadow: '0 10px 25px -5px rgba(0, 102, 204, 0.4)',
          },
          _active: {
            bg: 'brand.700',
            transform: 'translateY(0)',
          },
          _focus: {
            boxShadow: '0 0 0 3px rgba(0, 102, 204, 0.3)',
          },
        },
        outline: {
          borderColor: 'brand.500',
          color: 'brand.500',
          fontWeight: '600',
          bg: 'white',
          _hover: {
            bg: 'brand.50',
            borderColor: 'brand.600',
            color: 'brand.600',
            transform: 'translateY(-1px)',
          },
        },
        ghost: {
          color: 'brand.500',
          bg: 'transparent',
          _hover: {
            bg: 'brand.50',
            color: 'brand.600',
          },
        },
        gradient: {
          bgGradient: 'linear(to-r, brand.500, secondary.500)',
          color: 'white',
          fontWeight: '600',
          _hover: {
            bgGradient: 'linear(to-r, brand.600, secondary.600)',
            transform: 'translateY(-1px)',
            boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.4)',
          },
        },
      },
      sizes: {
        lg: {
          fontSize: 'lg',
          px: 8,
          py: 4,
          borderRadius: 'lg',
        },
      },
    },
    
    Card: {
      baseStyle: {
        container: {
          borderRadius: 'xl',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid',
          borderColor: 'gray.200',
          bg: 'white',
          _hover: {
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            borderColor: 'brand.300',
          },
          transition: 'all 0.3s ease',
        },
      },
      variants: {
        elevated: {
          container: {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            _hover: {
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          },
        },
        filled: {
          container: {
            bg: 'gray.50',
            borderColor: 'gray.200',
          },
        },
      },
    },
    
    Heading: {
      baseStyle: {
        fontWeight: '700',
        color: 'gray.900', // Pure black for maximum contrast
        letterSpacing: '-0.025em',
      },
      sizes: {
        '2xl': {
          fontSize: ['3xl', '4xl', '5xl'],
          lineHeight: '1.1',
        },
        xl: {
          fontSize: ['2xl', '3xl'],
          lineHeight: '1.2',
        },
      },
    },
    
    Text: {
      baseStyle: {
        color: 'gray.800', // Very dark gray for excellent contrast
        lineHeight: '1.6',
      },
      variants: {
        muted: {
          color: 'gray.600', // Dark gray for secondary text
        },
        emphasis: {
          color: 'gray.900', // Pure black for emphasis
          fontWeight: '600',
        },
      },
    },
    
    Link: {
      baseStyle: {
        color: 'brand.500', // High contrast brand color
        fontWeight: '500',
        _hover: {
          textDecoration: 'none',
          color: 'brand.600',
        },
        _focus: {
          boxShadow: '0 0 0 3px rgba(0, 102, 204, 0.3)',
          borderRadius: 'md',
        },
      },
    },
    
    Badge: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: 'full',
        textTransform: 'none',
      },
      variants: {
        solid: {
          bg: 'brand.100',
          color: 'brand.800', // High contrast text
        },
        subtle: {
          bg: 'brand.50',
          color: 'brand.700', // High contrast text
          border: '1px solid',
          borderColor: 'brand.200',
        },
      },
    },
    
    Alert: {
      baseStyle: {
        container: {
          borderRadius: 'lg',
          border: '1px solid',
        },
      },
      variants: {
        info: {
          container: {
            bg: 'info.50',
            borderColor: 'info.200',
            color: 'info.800', // High contrast text
          },
        },
        success: {
          container: {
            bg: 'success.50',
            borderColor: 'success.200',
            color: 'success.800', // High contrast text
          },
        },
        warning: {
          container: {
            bg: 'warning.50',
            borderColor: 'warning.200',
            color: 'warning.800', // High contrast text
          },
        },
        error: {
          container: {
            bg: 'error.50',
            borderColor: 'error.200',
            color: 'error.800', // High contrast text
          },
        },
      },
    },
    Modal: {
      baseStyle: (props: any) => ({
        dialog: {
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
        },
      }),
    },
  },
  
  // Global Styles with Better Contrast
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'white',
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
      },
      '::selection': {
        bg: 'brand.200',
        color: 'brand.900',
      },
    }),
  },
  
  // Enhanced Shadows for Better Depth
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    brand: '0 10px 25px -5px rgba(0, 102, 204, 0.4)',
    secondary: '0 10px 25px -5px rgba(124, 58, 237, 0.4)',
  },
  
  // Enhanced Border Radius
  radii: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
});

export default theme; 